import { escapeHtml, isTrusted, markTrusted } from '../ContentComposer/ContentComposer.js';
import {
  isForbiddenTag,
  isVoidTag,
  URL_ATTRS,
  safeUrl
} from './Constants.js';

/**
 * @param {*} value
 * @param {object} [ctx]
 * @returns {*}
 */
function resolve(value, ctx) {
  if (typeof value === 'function') {
    try {
      return value(ctx || {});
    } catch (err) {
      console.warn('[ElementBuilder] functional prop threw; using undefined.', err);
      return undefined;
    }
  }
  return value;
}

/**
 * @param {string|string[]|function|undefined} classVal
 * @param {object} [ctx]
 * @returns {string}
 */
function resolveClass(classVal, ctx) {
  const v = resolve(classVal, ctx);
  if (v == null || v === false) return '';
  if (Array.isArray(v)) {
    return v.filter(Boolean).map(String).join(' ').trim();
  }
  return String(v).trim();
}

/**
 * @param {Record<string, any>|undefined} style
 * @param {object} [ctx]
 * @returns {string}
 */
function resolveStyle(style, ctx) {
  const obj = resolve(style, ctx);
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return '';
  return Object.entries(obj)
    .filter(([, val]) => typeof val === 'string' || typeof val === 'number')
    .map(([k, val]) => `${escapeHtml(String(k))}:${escapeHtml(String(val))}`)
    .join(';');
}

/**
 * @param {string} key
 * @returns {string}
 */
function toDataAttr(key) {
  const k = String(key);
  if (k.startsWith('data-')) return k;
  const kebab = k.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
  return `data-${kebab}`;
}

/**
 * @param {Record<string, any>} attrs
 * @param {object} [ctx]
 * @returns {string}
 */
function serializeAttrs(attrs, ctx) {
  const obj = resolve(attrs, ctx);
  if (!obj || typeof obj !== 'object') return '';

  let out = '';
  for (const [rawKey, rawVal] of Object.entries(obj)) {
    const name = String(rawKey);
    if (!name) continue;
    if (/^on/i.test(name)) {
      console.warn(`[ElementBuilder] Ignoring event attribute "${name}".`);
      continue;
    }

    const val = resolve(rawVal, ctx);
    if (val === false || val == null) continue;

    if (val === true) {
      out += ` ${escapeHtml(name)}`;
      continue;
    }

    let str = String(val);
    if (URL_ATTRS.has(name.toLowerCase())) {
      str = safeUrl(str);
    }
    out += ` ${escapeHtml(name)}="${escapeHtml(str)}"`;
  }
  return out;
}

/**
 * @param {Record<string, any>|undefined} data
 * @param {object} [ctx]
 * @returns {string}
 */
function serializeData(data, ctx) {
  const obj = resolve(data, ctx);
  if (!obj || typeof obj !== 'object') return '';

  let out = '';
  for (const [key, rawVal] of Object.entries(obj)) {
    const val = resolve(rawVal, ctx);
    if (val === false || val == null) continue;
    const name = toDataAttr(key);
    if (val === true) {
      out += ` ${escapeHtml(name)}`;
      continue;
    }
    out += ` ${escapeHtml(name)}="${escapeHtml(String(val))}"`;
  }
  return out;
}

/**
 * @param {*} children
 * @param {object} [ctx]
 * @param {boolean} [allowHtml]
 * @returns {string}
 */
function renderChildren(children, ctx, allowHtml = false) {
  const resolved = resolve(children, ctx);
  if (resolved == null || resolved === false) return '';

  if (Array.isArray(resolved)) {
    return resolved.map((c) => renderChildren(c, ctx, allowHtml)).join('');
  }

  if (typeof resolved === 'object' && resolved.tag) {
    return render(resolved, ctx);
  }

  if (typeof resolved === 'string') {
    if (allowHtml && isTrusted(resolved)) return resolved;
    if (isTrusted(resolved)) return resolved;
    return escapeHtml(resolved);
  }

  if (typeof resolved === 'number' || typeof resolved === 'boolean') {
    return escapeHtml(String(resolved));
  }

  return '';
}

/**
 * Render an ElementConfig tree to a trusted HTML string.
 * @param {object|string|number|boolean|Array|null|undefined} config
 * @param {object} [ctx]
 * @returns {string}
 */
export function render(config, ctx = {}) {
  if (config == null) return '';

  if (typeof config === 'string' || typeof config === 'number' || typeof config === 'boolean') {
    return escapeHtml(String(config));
  }

  if (Array.isArray(config)) {
    return markTrusted(config.map((c) => render(c, ctx)).join(''));
  }

  if (typeof config !== 'object') return '';

  if (typeof config.showIf === 'function') {
    try {
      if (!config.showIf(ctx)) return '';
    } catch (err) {
      console.warn('[ElementBuilder] showIf threw; treating as visible.', err);
    }
  }

  const tag = String(config.tag || '').toLowerCase().trim();
  if (!tag) return '';

  if (isForbiddenTag(tag)) {
    console.warn(`[ElementBuilder] Forbidden tag "${tag}" suppressed.`);
    return '';
  }

  const classVal = config.class != null ? config.class : config.className;
  const classStr = resolveClass(classVal, ctx);
  const styleStr = resolveStyle(config.style, ctx);

  let attrStr = '';
  if (classStr) attrStr += ` class="${escapeHtml(classStr)}"`;
  if (styleStr) attrStr += ` style="${styleStr}"`;
  attrStr += serializeAttrs(config.attrs, ctx);
  attrStr += serializeData(config.data, ctx);

  if (isVoidTag(tag)) {
    return markTrusted(`<${tag}${attrStr}>`);
  }

  const allowHtml = Boolean(config.html);
  const inner = renderChildren(config.children, ctx, allowHtml);
  return markTrusted(`<${tag}${attrStr}>${inner}</${tag}>`);
}

/**
 * Render many configs, optionally wrapped in a flex container.
 * @param {Array} configs
 * @param {{ gap?: string, wrapper?: object|boolean, ctx?: object }} [options]
 * @returns {string}
 */
export function renderMany(configs, options = {}) {
  const list = Array.isArray(configs) ? configs : [];
  const ctx = options.ctx || {};

  if (options.wrapper === false) {
    return markTrusted(list.map((c) => render(c, ctx)).filter(Boolean).join(''));
  }

  if (options.wrapper && typeof options.wrapper === 'object') {
    return render({
      ...options.wrapper,
      children: options.wrapper.children != null ? options.wrapper.children : list
    }, ctx);
  }

  const gap = options.gap != null ? options.gap : '4px';
  return render({
    tag: 'div',
    style: {
      display: 'inline-flex',
      gap: String(gap),
      alignItems: 'center',
      flexWrap: 'nowrap'
    },
    children: list
  }, ctx);
}
